import { type PeerInfo, type Presence, RoomError, type ServerMsg } from "./protocol.ts";
import type { RoomBackend } from "./room.ts";

/**
 * Project presence: one presence-only room per project (`project:<projectId>`). Who is in the
 * project, where they are, what they look at, their cursor on a shared surface, and whom they
 * follow. Ephemeral: nothing here is stored, and the room has no ops.
 *
 * On the wire it is the room's ordinary `presence` message; the room validates it with
 * `parseProjectPresence` (unknown keys are dropped, a malformed payload is answered with an
 * `error` frame `invalid_presence` and not relayed). Every send replaces the peer's whole
 * presence, so a client sends its full state each time.
 */

export const WHERE_KINDS = [
  "project",
  "folder",
  "episode",
  "scene",
  "shot",
  "element",
  "canvas",
  "take",
  "asset",
] as const;
export type WhereKind = (typeof WHERE_KINDS)[number];

/** The item a person has open (the shell's current location). */
export interface PresenceWhere {
  kind: WhereKind;
  id: string;
  /** Display label, so others can show "Viewing: Shot 12" without a lookup. */
  label?: string;
}

/** Which take/version is on the stage; `t` is the playhead in seconds for video. */
export interface PresenceViewing {
  assetVersionId: string;
  t?: number;
}

export type CursorSpace = "stage" | "canvas";

/**
 * Pointer on a shared surface. On the stage x/y are normalised 0–1 over the image or video frame;
 * on a canvas they are board coordinates. `target` is an optional node or region id under it.
 */
export interface PresenceCursor {
  x: number;
  y: number;
  space: CursorSpace;
  target?: string;
}

export interface ProjectPresence {
  where?: PresenceWhere;
  viewing?: PresenceViewing;
  /** null: the pointer left the surface. */
  cursor?: PresenceCursor | null;
  /** Peer id (connection, not user) being followed; null or absent: not following. */
  following?: string | null;
  selection?: string[];
  /** Typing in a comment thread. */
  typing?: { threadId: string } | null;
}

/** A peer as the UI holds it: identity plus its last presence. */
export type PresencePeer = PeerInfo & { presence: ProjectPresence | null };

export const PRESENCE_LIMITS = {
  idLength: 100,
  labelLength: 200,
  selection: 200,
  /** Canvas coordinates are board units; anything beyond this is a client bug. */
  canvasCoord: 1e7,
} as const;

/** Room id of a project's presence room. Never collides with a canvas room (a bare uuid). */
export const PROJECT_ROOM_PREFIX = "project:";
export const projectRoomId = (projectId: string): string =>
  `${PROJECT_ROOM_PREFIX}${projectId.toLowerCase()}`;
/** The project id of a project room id, or null when `roomId` is not one. */
export const projectIdOfRoom = (roomId: string): string | null =>
  roomId.startsWith(PROJECT_ROOM_PREFIX) ? roomId.slice(PROJECT_ROOM_PREFIX.length) : null;
/** Path of a project's presence socket (Node server and Worker both serve it). */
export const projectRealtimePath = (projectId: string): string =>
  `/v1/realtime/projects/${projectId.toLowerCase()}`;

// ─── validation ──────────────────────────────────────────────────────────────

const bad = (what: string): never => {
  throw new RoomError("invalid_presence", what);
};
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const id = (v: unknown, what: string): string =>
  typeof v === "string" && v.length > 0 && v.length <= PRESENCE_LIMITS.idLength ? v : bad(what);
const finite = (v: unknown, what: string): number =>
  typeof v === "number" && Number.isFinite(v) ? v : bad(what);
const KINDS = new Set<string>(WHERE_KINDS);

/**
 * Validates a presence payload against the `ProjectPresence` contract and returns a clean copy
 * with only the known keys. Throws RoomError("invalid_presence") on anything malformed.
 */
export function parseProjectPresence(raw: unknown): ProjectPresence {
  if (!isObj(raw)) return bad("presence must be an object");
  const out: ProjectPresence = {};

  if (raw.where !== undefined) {
    const w = raw.where;
    if (!isObj(w)) return bad("where must be an object");
    if (typeof w.kind !== "string" || !KINDS.has(w.kind)) return bad("where.kind is not a known kind");
    out.where = { kind: w.kind as WhereKind, id: id(w.id, "where.id") };
    if (w.label !== undefined) {
      if (typeof w.label !== "string" || w.label.length > PRESENCE_LIMITS.labelLength)
        return bad("where.label must be a short string");
      out.where.label = w.label;
    }
  }

  if (raw.viewing !== undefined) {
    const v = raw.viewing;
    if (!isObj(v)) return bad("viewing must be an object");
    out.viewing = { assetVersionId: id(v.assetVersionId, "viewing.assetVersionId") };
    if (v.t !== undefined) {
      const t = finite(v.t, "viewing.t");
      if (t < 0) return bad("viewing.t must be >= 0");
      out.viewing.t = t;
    }
  }

  if (raw.cursor !== undefined) {
    const c = raw.cursor;
    if (c === null) out.cursor = null;
    else {
      if (!isObj(c)) return bad("cursor must be an object or null");
      if (c.space !== "stage" && c.space !== "canvas") return bad("cursor.space must be stage or canvas");
      const x = finite(c.x, "cursor.x");
      const y = finite(c.y, "cursor.y");
      if (c.space === "stage" && (x < 0 || x > 1 || y < 0 || y > 1))
        return bad("stage cursor is normalised 0–1");
      if (Math.abs(x) > PRESENCE_LIMITS.canvasCoord || Math.abs(y) > PRESENCE_LIMITS.canvasCoord)
        return bad("cursor out of range");
      out.cursor = { x, y, space: c.space };
      if (c.target !== undefined) out.cursor.target = id(c.target, "cursor.target");
    }
  }

  if (raw.following !== undefined) {
    out.following = raw.following === null ? null : id(raw.following, "following");
  }

  if (raw.selection !== undefined) {
    const s = raw.selection;
    if (!Array.isArray(s) || s.length > PRESENCE_LIMITS.selection)
      return bad(`selection must be an array of at most ${PRESENCE_LIMITS.selection} ids`);
    out.selection = s.map((x) => id(x, "selection item"));
  }

  if (raw.typing !== undefined) {
    const t = raw.typing;
    if (t === null) out.typing = null;
    else {
      if (!isObj(t)) return bad("typing must be an object or null");
      out.typing = { threadId: id(t.threadId, "typing.threadId") };
    }
  }

  return out;
}

/** Lenient read for clients: the presence, or null when it does not fit the contract. */
export function readProjectPresence(raw: unknown): ProjectPresence | null {
  try {
    return parseProjectPresence(raw);
  } catch {
    return null;
  }
}

// ─── the presence-only backend ───────────────────────────────────────────────

/**
 * Room backend for a presence-only room: no ops and no database writes. `canSee` decides at hello
 * whether the user may be in the room (for a project: an RLS read of the project row).
 */
export function createPresenceBackend(canSee: (userId: string) => Promise<boolean>): RoomBackend {
  return {
    readOnly: true,
    presence: (p) => parseProjectPresence(p) as Presence,
    async snapshot(userId) {
      if (!(await canSee(userId))) throw new RoomError("not_found", "project not found");
      return { rev: 0, snapshot: {} };
    },
    async opsSince() {
      return [];
    },
    async apply() {
      throw new RoomError("read_only", "this room carries presence only");
    },
  };
}

// ─── pure helpers for the UI ─────────────────────────────────────────────────

/** Same item: kind and id match (ids compared case-insensitively, labels ignored). */
export function samePlace(a: PresenceWhere | undefined | null, b: PresenceWhere | undefined | null): boolean {
  return !!a && !!b && a.kind === b.kind && a.id.toLowerCase() === b.id.toLowerCase();
}

/** Peers that have `where` open (who is in this folder, on this shot). */
export function peersAt<P extends PresencePeer>(
  peers: Iterable<P>,
  where: Pick<PresenceWhere, "kind" | "id">,
): P[] {
  const out: P[] = [];
  for (const p of peers) if (samePlace(p.presence?.where, where)) out.push(p);
  return out;
}

/** Peers with this take/version on their stage. */
export function peersViewing<P extends PresencePeer>(peers: Iterable<P>, assetVersionId: string): P[] {
  const v = assetVersionId.toLowerCase();
  const out: P[] = [];
  for (const p of peers) if (p.presence?.viewing?.assetVersionId.toLowerCase() === v) out.push(p);
  return out;
}

/** One entry per person (a user with two tabs is one avatar), in first-seen order. */
export function uniqueUsers<P extends PresencePeer>(peers: Iterable<P>): P[] {
  const seen = new Set<string>();
  const out: P[] = [];
  for (const p of peers) {
    if (seen.has(p.userId)) continue;
    seen.add(p.userId);
    out.push(p);
  }
  return out;
}

/**
 * The peer `self` follows, or null when not following, following itself, or the leader left.
 * When the leader follows someone too, the chain is walked to the person actually leading
 * (cycles stop at the last peer before the loop closes).
 */
export function followTarget<P extends PresencePeer>(
  peers: Iterable<P>,
  self: { id: string; presence?: ProjectPresence | null },
): P | null {
  const byId = new Map<string, P>();
  for (const p of peers) byId.set(p.id, p);
  const visited = new Set<string>([self.id]);
  let next = self.presence?.following ?? null;
  let leader: P | null = null;
  while (next && !visited.has(next)) {
    const p = byId.get(next);
    if (!p) break;
    visited.add(next);
    leader = p;
    next = p.presence?.following ?? null;
  }
  return leader;
}

/** Peers following `peerId` directly. */
export function followersOf<P extends PresencePeer>(peers: Iterable<P>, peerId: string): P[] {
  const out: P[] = [];
  for (const p of peers) if (p.presence?.following === peerId && p.id !== peerId) out.push(p);
  return out;
}

/** Where a follower should go: the leader's location and stage (null when there is no leader). */
export function followView(leader: PresencePeer | null): Pick<ProjectPresence, "where" | "viewing"> | null {
  if (!leader?.presence) return null;
  const { where, viewing } = leader.presence;
  return { ...(where ? { where } : {}), ...(viewing ? { viewing } : {}) };
}

/**
 * Cursors to draw for `self`: only peers on the same surface. On the stage both must have the same
 * version on it; on a canvas both must have the same canvas open. Yourself and other tabs of the
 * same user are left out.
 */
export function visibleCursors<P extends PresencePeer>(
  peers: Iterable<P>,
  self: { id: string; userId: string; presence?: ProjectPresence | null },
): { peer: P; cursor: PresenceCursor }[] {
  const mine = self.presence;
  const out: { peer: P; cursor: PresenceCursor }[] = [];
  for (const p of peers) {
    const c = p.presence?.cursor;
    if (!c || p.id === self.id || p.userId === self.userId) continue;
    if (c.space === "stage") {
      const a = mine?.viewing?.assetVersionId;
      const b = p.presence?.viewing?.assetVersionId;
      if (!a || !b || a.toLowerCase() !== b.toLowerCase()) continue;
    } else {
      const a = mine?.where;
      const b = p.presence?.where;
      if (a?.kind !== "canvas" || !samePlace(a, b)) continue;
    }
    out.push({ peer: p, cursor: c });
  }
  return out;
}

/** Peers typing in a thread. */
export function typingIn<P extends PresencePeer>(peers: Iterable<P>, threadId: string): P[] {
  const out: P[] = [];
  for (const p of peers) if (p.presence?.typing?.threadId === threadId) out.push(p);
  return out;
}

// ─── client-side roster ──────────────────────────────────────────────────────

/** What a client knows about a presence room, built from the server's messages. */
export interface PresenceRoster {
  self: PeerInfo | null;
  /** Other peers by id (never includes self). */
  peers: Record<string, PresencePeer>;
}

export const emptyRoster = (): PresenceRoster => ({ self: null, peers: {} });

/**
 * Folds one server message into the roster (immutably). Messages that do not concern presence
 * return the same object. Presence that does not fit the contract is kept as null.
 */
export function reduceRoster(state: PresenceRoster, msg: ServerMsg): PresenceRoster {
  switch (msg.t) {
    case "welcome": {
      const peers: Record<string, PresencePeer> = {};
      for (const { presence, ...info } of msg.peers)
        peers[info.id] = { ...info, presence: presence ? readProjectPresence(presence) : null };
      return { self: msg.self, peers };
    }
    case "join":
      if (msg.peer.id === state.self?.id) return state;
      return {
        ...state,
        peers: { ...state.peers, [msg.peer.id]: { ...msg.peer, presence: null } },
      };
    case "leave": {
      if (!(msg.peer in state.peers)) return state;
      const { [msg.peer]: _gone, ...rest } = state.peers;
      return { ...state, peers: rest };
    }
    case "presence": {
      const p = state.peers[msg.peer];
      if (!p) return state;
      return {
        ...state,
        peers: { ...state.peers, [msg.peer]: { ...p, presence: readProjectPresence(msg.presence) } },
      };
    }
    default:
      return state;
  }
}
